import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, EnvelopeSimple, Lock, User, Eye, EyeSlash } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      setEmail('');
      setPassword('');
      setName('');
      setError('');
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="auth-modal"
          >
            {/* Header */}
            <div className="relative bg-[#B46C4A] text-white p-6 pb-12">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                data-testid="auth-close-btn"
              >
                <X size={18} weight="bold" />
              </button>
              <h2 className="text-2xl font-['Outfit'] font-semibold">
                {mode === 'login' ? 'Welcome Back' : 'Join Safe Space'}
              </h2>
              <p className="text-white/80 mt-1 text-sm">
                {mode === 'login' ? 'Sign in to continue your journey' : 'Create an account to save your conversations'}
              </p>
            </div>

            {/* Form */}
            <div className="p-6 -mt-6">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 text-red-600 text-sm p-3 rounded-xl mb-4 border border-red-100"
                    data-testid="auth-error"
                  >
                    {error}
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {mode === 'register' && (
                    <div>
                      <label className="block text-sm font-medium text-[#5C564F] mb-2">Name</label>
                      <div className="relative">
                        <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C564F]" />
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your name"
                          className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-xl pl-12 pr-4 py-3 text-[#2A2825] placeholder:text-[#5C564F]/50 focus:outline-none focus:ring-2 focus:ring-[#B46C4A]/20 focus:border-[#B46C4A]"
                          data-testid="auth-name-input"
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-[#5C564F] mb-2">Email</label>
                    <div className="relative">
                      <EnvelopeSimple size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C564F]" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-xl pl-12 pr-4 py-3 text-[#2A2825] placeholder:text-[#5C564F]/50 focus:outline-none focus:ring-2 focus:ring-[#B46C4A]/20 focus:border-[#B46C4A]"
                        data-testid="auth-email-input"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#5C564F] mb-2">Password</label>
                    <div className="relative">
                      <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C564F]" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-xl pl-12 pr-12 py-3 text-[#2A2825] placeholder:text-[#5C564F]/50 focus:outline-none focus:ring-2 focus:ring-[#B46C4A]/20 focus:border-[#B46C4A]"
                        data-testid="auth-password-input"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5C564F] hover:text-[#2A2825]"
                      >
                        {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#B46C4A] hover:bg-[#9B5C3E] disabled:bg-[#E5DCD0] text-white font-medium py-3 rounded-xl transition-all duration-300"
                    data-testid="auth-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        Please wait...
                      </span>
                    ) : (
                      mode === 'login' ? 'Sign In' : 'Create Account'
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-[#5C564F]">
                    {mode === 'login' ? "Don't have an account?" : "Already have an account?"}
                    <button
                      onClick={toggleMode}
                      className="ml-1 text-[#B46C4A] hover:text-[#9B5C3E] font-medium"
                      data-testid="auth-toggle-mode"
                    >
                      {mode === 'login' ? 'Sign up' : 'Sign in'}
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
